import type { PatientRouteParams } from 'app/routes';
import { CrudForm } from 'components/form';
import type { Field } from 'components/types';
import { PatientHeaderSection } from 'core/patient';
import { SearchWarningCard } from 'core/search';
import {
  FormCard,
  type RowConfig,
  Section,
  SkeletonProvider,
} from 'design-system';
import { useGlobalSearchParams } from 'expo-router';
import { PatientApi } from 'integration/aggregate';
import type { PatientDrawerScreenProps } from 'navigation';
import type React from 'react';
import { useCallback, useState } from 'react';
import { ScrollView } from 'react-native';
import { strings } from 'strings';

type FormType = {
  query: string;
};

const patientApi = new PatientApi();

export const PatientSearchScreen: React.FC<
  PatientDrawerScreenProps<'Search'>
> = () => {
  const { patientId } = useGlobalSearchParams<PatientRouteParams>();
  const [warningHidden, setWarningHidden] = useState(false);
  const [summary, setSummary] = useState('');

  const fields: Field<FormType>[] = [
    {
      subfields: [
        {
          type: 'text-box',
          key: 'query',
          label: strings.PatientSearchFormLabel,
          placeholder: strings.PatientSearchFormPlaceholder,
        },
      ],
    },
  ];

  const onSubmit = useCallback(
    async (data: FormType) => {
      try {
        setSummary('');

        const result = await patientApi.search({
          id: patientId,
          search: data.query,
        });
        setSummary(result.summary);

        return {};
      } catch {
        throw new Error('Patient search found no results');
      }
    },
    [patientId],
  );

  const summaryRow: RowConfig[] = [
    {
      fields: [
        {
          type: 'text',
          label: '',
          display: summary,
        },
      ],
    },
  ];

  const isLoading = false;
  return (
    <ScrollView>
      <PatientHeaderSection id={patientId} />

      <Section isVisible={!warningHidden}>
        <SearchWarningCard setIsHidden={setWarningHidden} />
      </Section>

      <Section>
        <CrudForm
          cardProps={{ unsetZIndex: true }}
          title={strings.PatientSearchFormTitle}
          buttonText={strings.PatientSearchFormButton}
          buttonIcon="search"
          fields={fields}
          initialValues={{ query: '' }}
          onSubmit={onSubmit}
          onChangeDebounceTime={0}
        />
      </Section>
      <Section isVisible={summary.length > 0}>
        <FormCard
          rows={summaryRow}
          title={strings.PatientSearchResultCardTitle}
          showIcons={false}
        />
      </Section>
      <SkeletonProvider loading={isLoading}></SkeletonProvider>
    </ScrollView>
  );
};
